import { getRedirectQueryString } from './queryString'

export enum ChainalysisProduct {
  SELF_SERVICE_ADMIN = 'admin',
  INTERNAL_ADMIN = 'internaladmin',
  KYT = 'kyt',
  KRYPTOS = 'kryptos',
  STORYLINE = 'storyline',
  SANDBOX = 'sandbox',
  FIELD = 'field',
  FIELD_AI = 'field-ai',
  REACTOR = 'reactor',
  REACTOR_US = 'reactor-us',
  IAPIDOCS = 'apidocumentation',
  KNOWLEDGE_BASE = 'knowledgebase',
  PLAYBOOK = 'playbook',
  ACADEMY = 'academy',
  TRAINING = 'training',
  DIALOG = 'dialog',
  DIALOG_EU = 'dialog-eu',
  DIALOG_US = 'dialog-us',
  DIALOG_CGS = 'dialog-cgs',
  CHAIN_INTERNAL = 'chain-internal',
  CHAIN_INTERNAL_US = 'chain-internal-us',
  CHAIN = 'chain',
  CHAIN_US = 'chain-us',
  RAPID = 'rapid',
  DATA_SOLUTIONS = 'data_solutions',
  HEXAGATE = 'hexagate',
}

export interface SSOConfig {
  clientId: string
  scope: string
  redirectUri: string
  domain: string
  audience: string
  defaultConnection: string
}

// ---------------------------------------------------------------------------
// Runtime config — KaaS containers inject window.runtimeConfig via
// runtime.conf.js at startup. REACT_APP_* process.env vars are the fallback
// for local dev and the legacy S3 deployment path.
// All feature-flag vars are available via runtimeConfig so they can be
// toggled per environment without rebuilding the Docker image.
// process.env.REACT_APP_* fallbacks remain for local dev / legacy S3 builds.
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    runtimeConfig?: Record<string, string>
  }
}

const rc: Record<string, string> = (typeof window !== 'undefined' && window.runtimeConfig) || {}

/** Read a URL/domain config value: runtimeConfig first, process.env fallback. */
const runtimeEnv = (rcKey: string, envKey: string, fallback = ''): string =>
  rc[rcKey] || process.env[envKey] || fallback

// ---------------------------------------------------------------------------

// Check if the URL is valid
// https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
const isValidUrl = (url: string) => {
  try {
    new URL(url)
    return true
  } catch (e) {
    return false
  }
}

const validateProductAppByOrigin = (origin: string, hostName: string) => {
  if (
    (hostName === 'localhost' || hostName.includes('localhost.chainalysis.com')) &&
    runtimeEnv('allowLocalhostRedirect', 'REACT_APP_ALLOW_LOCALHOST_REDIRECT') === 'true'
  ) {
    return true
  }

  return (
    origin === runtimeEnv('kyt', 'REACT_APP_KYT') ||
    origin === runtimeEnv('kryptos', 'REACT_APP_KRYPTOS') ||
    origin === runtimeEnv('storyline', 'REACT_APP_STORYLINE') ||
    origin === runtimeEnv('sandbox', 'REACT_APP_SANDBOX') ||
    origin === runtimeEnv('field', 'REACT_APP_FIELD') ||
    origin === runtimeEnv('fieldAi', 'REACT_APP_FIELD_AI') ||
    origin === runtimeEnv('selfServiceAdmin', 'REACT_APP_SELF_SERVICE_ADMIN') ||
    origin === runtimeEnv('internalAdmin', 'REACT_APP_INTERNAL_ADMIN') ||
    origin === runtimeEnv('reactor', 'REACT_APP_REACTOR') ||
    origin === runtimeEnv('reactorUs', 'REACT_APP_REACTOR_US') ||
    origin === runtimeEnv('playbook', 'REACT_APP_PLAYBOOK') ||
    origin === runtimeEnv('academy', 'REACT_APP_ACADEMY') ||
    origin === runtimeEnv('training', 'REACT_APP_TRAINING') ||
    origin === runtimeEnv('dialog', 'REACT_APP_DIALOG') ||
    origin === runtimeEnv('dialogEu', 'REACT_APP_DIALOG_EU') ||
    origin === runtimeEnv('dialogUs', 'REACT_APP_DIALOG_US') ||
    origin === runtimeEnv('dialogCgs', 'REACT_APP_DIALOG_CGS') ||
    origin === runtimeEnv('chainInternal', 'REACT_APP_CHAIN_INTERNAL') ||
    origin === runtimeEnv('chainInternalUs', 'REACT_APP_CHAIN_INTERNAL_US') ||
    origin === runtimeEnv('chain', 'REACT_APP_CHAIN') ||
    origin === runtimeEnv('chainUs', 'REACT_APP_CHAIN_US') ||
    origin === runtimeEnv('rapid', 'REACT_APP_RAPID') ||
    origin === runtimeEnv('shell', 'REACT_APP_SHELL') ||
    origin === runtimeEnv('dataSolutions', 'REACT_APP_DATA_SOLUTIONS') ||
    origin === runtimeEnv('iapidocs', 'REACT_APP_IAPIDOCS') ||
    origin === runtimeEnv('knowledgeBase', 'REACT_APP_KNOWLEDGE_BASE') ||
    origin === runtimeEnv('hexagate', 'REACT_APP_HEXAGATE')
  )
}

const getAppEnvironment = (app: string): string => {
  switch (app?.toLowerCase()) {
    case ChainalysisProduct.KYT:
      return runtimeEnv('kyt', 'REACT_APP_KYT', 'https://chainalysis.com')
    case ChainalysisProduct.KRYPTOS:
      return runtimeEnv('kryptos', 'REACT_APP_KRYPTOS', 'https://chainalysis.com')
    case ChainalysisProduct.STORYLINE:
      return runtimeEnv('storyline', 'REACT_APP_STORYLINE', 'https://chainalysis.com')
    case ChainalysisProduct.SANDBOX:
      return runtimeEnv('sandbox', 'REACT_APP_SANDBOX', 'https://chainalysis.com')
    case ChainalysisProduct.FIELD:
      return runtimeEnv('field', 'REACT_APP_FIELD', 'https://chainalysis.com')
    case ChainalysisProduct.FIELD_AI:
      return runtimeEnv('fieldAi', 'REACT_APP_FIELD_AI', 'https://chainalysis.com')
    case ChainalysisProduct.SELF_SERVICE_ADMIN:
      return runtimeEnv('selfServiceAdmin', 'REACT_APP_SELF_SERVICE_ADMIN', 'https://chainalysis.com')
    case ChainalysisProduct.INTERNAL_ADMIN:
      return runtimeEnv('internalAdmin', 'REACT_APP_INTERNAL_ADMIN', 'https://chainalysis.com')
    case ChainalysisProduct.IAPIDOCS:
      return runtimeEnv('iapidocs', 'REACT_APP_IAPIDOCS', 'https://chainalysis.com')
    case ChainalysisProduct.KNOWLEDGE_BASE:
      return runtimeEnv('knowledgeBase', 'REACT_APP_KNOWLEDGE_BASE', 'https://chainalysis.com')
    case ChainalysisProduct.REACTOR:
      return runtimeEnv('reactor', 'REACT_APP_REACTOR', 'https://chainalysis.com')
    case ChainalysisProduct.REACTOR_US:
      return runtimeEnv('reactorUs', 'REACT_APP_REACTOR_US', 'https://chainalysis.com')
    case ChainalysisProduct.PLAYBOOK:
      return runtimeEnv('playbook', 'REACT_APP_PLAYBOOK', 'https://chainalysis.com')
    case ChainalysisProduct.ACADEMY:
      return runtimeEnv('academy', 'REACT_APP_ACADEMY', 'https://chainalysis.com')
    case ChainalysisProduct.TRAINING:
      return runtimeEnv('training', 'REACT_APP_TRAINING', 'https://chainalysis.com')
    case ChainalysisProduct.DIALOG:
      return runtimeEnv('dialog', 'REACT_APP_DIALOG', 'https://chainalysis.com')
    case ChainalysisProduct.DIALOG_EU:
      return runtimeEnv('dialogEu', 'REACT_APP_DIALOG_EU', 'https://chainalysis.com')
    case ChainalysisProduct.DIALOG_US:
      return runtimeEnv('dialogUs', 'REACT_APP_DIALOG_US', 'https://chainalysis.com')
    case ChainalysisProduct.DIALOG_CGS:
      return runtimeEnv('dialogCgs', 'REACT_APP_DIALOG_CGS', 'https://chainalysis.com')
    case ChainalysisProduct.CHAIN_INTERNAL:
      return runtimeEnv('chainInternal', 'REACT_APP_CHAIN_INTERNAL', 'https://chainalysis.com')
    case ChainalysisProduct.CHAIN_INTERNAL_US:
      return runtimeEnv('chainInternalUs', 'REACT_APP_CHAIN_INTERNAL_US', 'https://chainalysis.com')
    case ChainalysisProduct.CHAIN:
      return runtimeEnv('chain', 'REACT_APP_CHAIN', 'https://chainalysis.com')
    case ChainalysisProduct.CHAIN_US:
      return runtimeEnv('chainUs', 'REACT_APP_CHAIN_US', 'https://chainalysis.com')
    case ChainalysisProduct.RAPID:
      return runtimeEnv('rapid', 'REACT_APP_RAPID', 'https://chainalysis.com')
    case ChainalysisProduct.DATA_SOLUTIONS:
      return runtimeEnv('dataSolutions', 'REACT_APP_DATA_SOLUTIONS', 'https://chainalysis.com')
    case ChainalysisProduct.HEXAGATE:
      return runtimeEnv('hexagate', 'REACT_APP_HEXAGATE', 'https://chainalysis.com')
    default:
      return runtimeEnv('allowAnyRedirect', 'REACT_APP_ALLOW_ANY_REDIRECT') === 'true' && app != '' && isValidUrl(app)
        ? app
        : runtimeEnv('reactor', 'REACT_APP_REACTOR', 'https://chainalysis.com')
  }
}

const getAvailableRedirect = (redirect: string, products: string[]): string => {
  if (runtimeEnv('allowAnyRedirect', 'REACT_APP_ALLOW_ANY_REDIRECT') === 'true' && isValidUrl(redirect)) {
    const redirectUrl = new URL(redirect)
    if (validateProductAppByOrigin(redirectUrl.origin, redirectUrl.hostname)) {
      return redirect
    }
  }

  if (
    redirect == ChainalysisProduct.IAPIDOCS ||
    redirect == ChainalysisProduct.KNOWLEDGE_BASE ||
    redirect == ChainalysisProduct.ACADEMY ||
    redirect == ChainalysisProduct.TRAINING ||
    redirect == ChainalysisProduct.DIALOG ||
    redirect == ChainalysisProduct.DIALOG_EU ||
    redirect == ChainalysisProduct.DIALOG_US ||
    redirect == ChainalysisProduct.DIALOG_CGS ||
    redirect == ChainalysisProduct.CHAIN_INTERNAL ||
    redirect == ChainalysisProduct.CHAIN_INTERNAL_US ||
    redirect == ChainalysisProduct.CHAIN ||
    redirect == ChainalysisProduct.CHAIN_US ||
    redirect == ChainalysisProduct.RAPID ||
    redirect == ChainalysisProduct.SANDBOX ||
    redirect == ChainalysisProduct.REACTOR_US ||
    redirect == ChainalysisProduct.DATA_SOLUTIONS
  )
    return getAppEnvironment(redirect)
  if (products?.findIndex(p => p.toLowerCase() == redirect) >= 0) return getAppEnvironment(redirect)
  if (products?.findIndex(p => p.toLowerCase() == 'kyt') >= 0) return getAppEnvironment('kyt')
  if (products?.findIndex(p => p.toLowerCase() == 'reactor') >= 0) return getAppEnvironment('reactor')
  if (products?.findIndex(p => p.toLowerCase() == 'rapid') >= 0) return getAppEnvironment('rapid')
  if (products?.findIndex(p => p.toLowerCase() == 'hexagate') >= 0) return getAppEnvironment('hexagate')
  if (products?.length > 0) return getAppEnvironment(products[0])
  if (!redirect) return getAppEnvironment(ChainalysisProduct.ACADEMY)
  return getAppEnvironment(redirect)
}

const getApiEnvironment = (): string => {
  return runtimeEnv('umApi', 'REACT_APP_UMAPI')
}

const getGQLApiEnvironment = (): string => {
  return runtimeEnv('graphqlApi', 'REACT_APP_GRAPHQL_API')
}

const getSSOConfig = (): SSOConfig => {
  const scope = 'openid email offline_access'
  const redirectUri = window.location.href
  const ssoDomain = runtimeEnv('ssoDomain', 'REACT_APP_SSO_DOMAIN', 'REACT_APP_SSO_DOMAIN required for SSO')
  return {
    domain: ssoDomain,
    clientId: runtimeEnv('clientId', 'REACT_APP_CHAINALYSIS_CLIENTID', 'Client Id is required for SSO'),
    redirectUri: redirectUri,
    scope,
    audience: runtimeEnv('audienceUrl', 'REACT_APP_AUDIENCE_URL', `https://${ssoDomain}/api/v2/`),
    defaultConnection: runtimeEnv(
      'defaultConnection',
      'REACT_APP_DEFAULT_CONNECTION',
      'User-Management-BE-Authentication'
    ),
  }
}

const getLoginURLWithRedirectString = (): string => {
  return `${window.location.protocol}//${window.location.hostname}:${
    window.location.port
  }/login?redirect=${getRedirectQueryString()}`
}

const getMarketingMarketoEnabled = (): boolean => {
  return runtimeEnv('marketingMarketoEnabled', 'REACT_APP_MARKETING_MARKETO_ENABLED') === 'true'
}

const isLegacyEnabled = (): boolean => {
  return runtimeEnv('legacyEnabled', 'REACT_APP_LEGACY_ENABLED') === 'true'
}

const isOAuthBootstrapEnabled = (): boolean => {
  return runtimeEnv('oauthBootstrapEnabled', 'REACT_APP_OAUTH_BOOTSTRAP_ENABLED') === 'true'
}

export {
  getApiEnvironment,
  getAppEnvironment,
  getSSOConfig,
  getAvailableRedirect,
  getLoginURLWithRedirectString,
  getGQLApiEnvironment,
  getMarketingMarketoEnabled,
  isLegacyEnabled,
  isOAuthBootstrapEnabled,
}
